"use client"

import * as React from "react"

import { monthLongName } from "@/features/finance/overview/church-f3/lib/fallback-period"
import { comparisonBasis } from "@/features/finance/overview/branch-f1/lib/branch-f1-format"
import { useFinanceOverviewPeriod } from "@/features/finance/overview/context/finance-overview-context"
import { useLocale, useTranslations } from "@/i18n"
import { PeriodPreset } from "@/shared/lib/dates/period-range"

/**
 * The words a period sentence needs: how to say "over this period" (`ce mois`, `en octobre` under a fallback,
 * else `sur la période`), and the comparison phrases against the period before the one shown.
 */
export function useBoardWording(shown: { from: string; to: string } | null, isFallback: boolean) {
  const t = useTranslations("finance.overview.branch")
  const locale = useLocale()
  const board = useFinanceOverviewPeriod()
  return React.useMemo(() => {
    const range = shown ?? (board.from && board.to ? { from: board.from, to: board.to } : null)
    const fallbackMonth = isFallback && range && range.from.slice(0, 7) === range.to.slice(0, 7) ? range.from.slice(0, 7) : null
    const during = fallbackMonth
      ? t("period.inMonth", { month: monthLongName(fallbackMonth, locale).toLocaleLowerCase(locale) })
      : !isFallback && board.preset === PeriodPreset.MonthToDate
        ? t("period.thisMonth")
        : t("period.thisPeriod")
    const compared = range ? comparisonBasis(range.from, range.to) : null
    const month = compared?.month ? monthLongName(compared.month, locale).toLocaleLowerCase(locale) : ""
    const basis = compared?.basis
    return {
      /** "ce mois", "en octobre", "sur la période". */
      during,
      /** "comme en octobre". */
      same: () => t(`compare.same.${basis ?? "period"}`, { month }),
      /** "par rapport à octobre". */
      versus: () => t(`compare.versus.${basis ?? "period"}`, { month }),
      /** "contre 40 % en octobre". */
      against: (value: number) => t(`compare.against.${basis ?? "period"}`, { month, value }),
    }
  }, [shown, isFallback, board.from, board.to, board.preset, t, locale])
}
